import {
  applySnapshot,
  flow,
  types,
  getSnapshot,
  SnapshotOrInstance,
} from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { NutritionCategoryModel, PUBLISH_STATUSES } from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { MSURL } from "utils/ms";
import { getNextPublishStatusWithoutScheduled } from "../utils/publish";
import { reorder } from "../utils/calculation";
import { REORDER_MOVE_TYPES } from "../constants/reorder";

const corePath = "/core/api/v1";
export const GreetingModel = types
  .model({
    id: types.number,
    url: types.string,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    order: types.number,
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        file: self.url,
      };
    },
  }));

const GreetingStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${corePath}/greeting`, params, undefined, MSURL);
  }),
  initialOrder: { column: "order", desc: true },
  itemModel: GreetingModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(GreetingModel),
    isMoreLoading: types.optional(types.boolean, false),
    isGreetingReordering: types.optional(types.boolean, false),
    isGreetingLoading: types.optional(types.boolean, false),
    isGreetingManaging: types.optional(types.boolean, false),
    isGreetingPublishing: types.optional(types.boolean, false),
    isGreetingCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof NutritionCategoryModel>[],
      categoryId: number,
    ) {
      const categoryIdNewIndex = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof NutritionCategoryModel>) =>
          item.id === categoryId,
      );

      switch (categoryIdNewIndex) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case self.items.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[categoryIdNewIndex - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },

    updateSection(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(
            `${corePath}/greeting`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              search: self.search,
              orderBy,
              orderByDesc,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: self.items.concat(items),
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchDetails: flow(function*(id?: number) {
      self.isGreetingLoading = true;

      try {
        const details = yield api.get(
          `${corePath}/greeting/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isGreetingLoading = false;
      }
    }),

    createGreeting: flow(function*(payload: any) {
      self.isGreetingManaging = true;

      try {
        const { id } = yield api.postFormData(
          `${corePath}/greeting`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isGreetingManaging = false;
      }
    }),

    editGreetingInfo: flow(function*(payload: any) {
      self.isGreetingManaging = true;

      try {
        return yield api.putFormData(
          `${corePath}/greeting/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isGreetingManaging = false;
      }
    }),

    reorderCategories: flow(function*({
      startIndex,
      endIndex,
    }: {
      startIndex: number;
      endIndex: number;
    }) {
      self.isGreetingReordering = true;
      const { items } = getSnapshot(self);

      try {
        const categoryId = items[startIndex].id;
        const reorderedItems = reorder(items, startIndex, endIndex);
        applySnapshot(self, { ...self, items: reorderedItems });

        const reorderInfo = self.getReorderInfo(reorderedItems, categoryId);
        console.log(reorderInfo);

        yield api.post(
          `${corePath}/greeting/move`,
          {
            id: categoryId,
            ...reorderInfo,
          },
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isGreetingReordering = false;
      }
    }),

    changeStatus: flow(function*(id: number, status: string) {
      self.isGreetingPublishing = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${corePath}/greeting/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateSection(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isGreetingPublishing = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default GreetingStore.create();
